import { computeGazeVector, computeHeadVector, type EventEmitter } from '@alphahub/eie-core';
import type { SessionStateStore } from './SessionStateStore';

type Getters = {
  getLatestLandmarks: () => Array<{ i: number; x: number; y: number; z: number }> | undefined;
  getSessionId: () => string;
  getConfig: () => any;
  getFacialDetector: () => any;
  getLocalClassifier: () => any;
};

type Setters = {
  clearLastStableEmotion: () => void;
  clearPendingLocalCalibration: () => void;
  runLightDetectionTick: () => void;
};

/**
 * LocalCalibrationRunner encapsulates the gaze/head sampling loop and
 * offset computation previously inlined in EIESession.startLocalCalibration.
 */
export class LocalCalibrationRunner {
  constructor(
    private emitter: EventEmitter,
    private state: SessionStateStore,
    private getters: Getters,
    private setters: Setters,
  ) {}

  start(screenAnchor?: { x: number; y: number }): void {
    const hasFaceReady = !!(this.getters.getLatestLandmarks()?.length);
    if (!hasFaceReady) {
      this.state.pendingLocalCalibration = { screenAnchor };
      console.info('[EIE][CAL] armed-waiting-for-face', {
        sessionId: this.getters.getSessionId(),
        screenAnchor: screenAnchor ?? null,
        landmarkCount: this.getters.getLatestLandmarks()?.length ?? 0,
      });
      return;
    }

    try {
      this.setters.clearPendingLocalCalibration();
      const config = this.getters.getConfig();
      const facialDetector = this.getters.getFacialDetector();
      const localClassifier = this.getters.getLocalClassifier();

      if (config.camera?.enableSessionFaceLock !== false && facialDetector?.lockCurrentFaceFromLandmarks) {
        const locked = facialDetector.lockCurrentFaceFromLandmarks(this.getters.getLatestLandmarks() as any);
        console.info(`[EIE][FACE_LOCK] calibration lock ${locked ? 'acquired' : 'not-acquired'}`, {
          sessionId: this.getters.getSessionId(),
          landmarkCount: this.getters.getLatestLandmarks()?.length ?? 0,
        });
      }
      this.emitter.emit('calibrationStarted', { sessionId: this.getters.getSessionId(), timestamp: Date.now() });
      localClassifier?.startCalibration?.();
      try { localClassifier?.setScreenCalibrationAnchor?.(screenAnchor ?? null); } catch {}
      console.info('[EIE][CAL] start', { sessionId: this.getters.getSessionId(), screenAnchor: screenAnchor ?? null });

      const duration = config.localClassifier?.calibrationDuration ?? 2500;
      const minSamples = 8;
      const maxWait = duration + 5000;
      const samples: Array<{ h: number; v: number }> = [];
      const headSamples: Array<{ yaw: number; pitch: number }> = [];
      const sampleInterval = 150;
      const requestStart = Date.now();
      let samplingStart: number | null = null;
      let lastWaitLogAt = 0;
      let lastProgressLoggedSamples = -1;

      const iv = setInterval(() => {
        const now = Date.now();
        let latestGaze: { h: number; v: number } | null = null;
        let latestHead: { yaw: number; pitch: number } | null = null;

        if (this.getters.getLatestLandmarks() && this.getters.getLatestLandmarks()!.length) {
          try {
            let ordered: any[] = [];
            const lms = this.getters.getLatestLandmarks() as any[];
            const hasIndex = lms.some(l => typeof l.i === 'number');
            if (hasIndex) {
              const maxIdx = Math.max(...lms.map(l => l.i));
              ordered = new Array(Math.max(469, maxIdx + 1)).fill(null).map(() => ({ x: 0, y: 0 }));
              for (const p of lms) ordered[p.i] = { x: p.x, y: p.y };
            } else {
              ordered = lms.map(p => ({ x: p.x, y: p.y }));
            }
            const gaze = computeGazeVector(ordered as any);
            const head = computeHeadVector(ordered as any);
            latestGaze = gaze;
            latestHead = head;

            if (gaze || head) {
              if (samplingStart === null) samplingStart = now;
            }
            if (gaze) samples.push({ h: gaze.h, v: gaze.v });
            if (head) headSamples.push({ yaw: head.yaw, pitch: head.pitch });

            const progressCount = Math.max(samples.length, headSamples.length);
            if (progressCount !== lastProgressLoggedSamples && (progressCount <= 3 || progressCount % 5 === 0)) {
              lastProgressLoggedSamples = progressCount;
              console.debug('[EIE][CAL] sample', {
                sampleCount: progressCount,
                elapsedMs: samplingStart === null ? 0 : now - samplingStart,
                gaze,
                head,
              });
            }
          } catch (err) {
            console.warn('[EIE][CAL] sample failed', err);
          }
        }

        const elapsedSinceSampling = samplingStart === null ? 0 : now - samplingStart;
        const elapsedSinceRequest = now - requestStart;
        const canFinalize = samplingStart !== null && elapsedSinceSampling >= duration && (samples.length >= minSamples || headSamples.length >= minSamples);
        const mustFinalize = elapsedSinceRequest >= maxWait;

        if (samplingStart === null && now - lastWaitLogAt >= 1000) {
          lastWaitLogAt = now;
          console.debug('[EIE][CAL] waiting-for-face', {
            requestedMs: elapsedSinceRequest,
            hasLandmarks: !!(this.getters.getLatestLandmarks() && this.getters.getLatestLandmarks()!.length),
            landmarkCount: this.getters.getLatestLandmarks()?.length ?? 0,
          });
        }

        this.emitter.emit('calibrationProgress', {
          sessionId: this.getters.getSessionId(),
          timestamp: now,
          waitingForFace: samplingStart === null,
          elapsedMs: elapsedSinceSampling,
          requestedMs: elapsedSinceRequest,
          durationMs: duration,
          sampleCount: samples.length,
          minSamples,
          latestGaze,
          latestHead,
          screenAnchor: screenAnchor ?? null,
        } as any);

        if (canFinalize || mustFinalize) {
          clearInterval(iv);
          console.info('[EIE][CAL] finalize', {
            reason: canFinalize ? 'complete' : 'timeout',
            samples: samples.length,
            headSamples: headSamples.length,
            elapsedSinceSampling,
            elapsedSinceRequest,
          });

          let gazeOffset: { h: number; v: number } | null = null;
          let medianOffset: { h: number; v: number } | null = null;
          let tolerance: { h: number; v: number } | null = null;
          let samplesKept = 0;

          if (samples.length > 0) {
            const hs = samples.map(s => s.h).sort((a, b) => a - b);
            const vs = samples.map(s => s.v).sort((a, b) => a - b);
            const medianFn = (arr: number[]) => {
              const mid = Math.floor(arr.length / 2);
              return arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
            };
            const medH = medianFn(hs);
            const medV = medianFn(vs);
            const madH = medianFn(hs.map(v => Math.abs(v - medH)).sort((a, b) => a - b));
            const madV = medianFn(vs.map(v => Math.abs(v - medV)).sort((a, b) => a - b));
            const tolH = Math.max(0.006, madH * 2.5);
            const tolV = Math.max(0.006, madV * 2.5);
            const filtered = samples.filter(s => Math.abs(s.h - medH) <= tolH && Math.abs(s.v - medV) <= tolV);
            const base = filtered.length >= 4 ? filtered : samples;
            const avg = base.reduce((a, b) => ({ h: a.h + b.h, v: a.v + b.v }), { h: 0, v: 0 });
            avg.h /= base.length;
            avg.v /= base.length;
            gazeOffset = avg;
            medianOffset = { h: medH, v: medV };
            tolerance = { h: tolH, v: tolV };
            samplesKept = base.length;
            try { localClassifier?.setGazeCalibrationOffset?.(avg); } catch {}
          } else if (headSamples.length > 0) {
            gazeOffset = { h: 0, v: 0 };
            try { localClassifier?.setGazeCalibrationOffset?.(gazeOffset); } catch {}
          }

          let headAvg: { yaw: number; pitch: number } | null = null;
          if (headSamples.length > 0) {
            const ys = headSamples.map(s => s.yaw).sort((a, b) => a - b);
            const ps = headSamples.map(s => s.pitch).sort((a, b) => a - b);
            const medianFn = (arr: number[]) => {
              const mid = Math.floor(arr.length / 2);
              return arr.length % 2 === 0 ? (arr[mid - 1] + arr[mid]) / 2 : arr[mid];
            };
            const medYaw = medianFn(ys);
            const medPitch = medianFn(ps);
            const madYaw = medianFn(ys.map(v => Math.abs(v - medYaw)).sort((a, b) => a - b));
            const madPitch = medianFn(ps.map(v => Math.abs(v - medPitch)).sort((a, b) => a - b));
            const tolYaw = Math.max(0.01, madYaw * 2.5);
            const tolPitch = Math.max(0.01, madPitch * 2.5);
            const filteredHead = headSamples.filter(s => Math.abs(s.yaw - medYaw) <= tolYaw && Math.abs(s.pitch - medPitch) <= tolPitch);
            const headBase = filteredHead.length >= 4 ? filteredHead : headSamples;
            headAvg = headBase.reduce((a, b) => ({ yaw: a.yaw + b.yaw, pitch: a.pitch + b.pitch }), { yaw: 0, pitch: 0 });
            headAvg.yaw /= headBase.length;
            headAvg.pitch /= headBase.length;
            try { localClassifier?.setHeadCalibrationOffset?.(headAvg); } catch {}
          }

          this.setters.clearLastStableEmotion();
          this.emitter.emit('calibrationFinished', {
            sessionId: this.getters.getSessionId(),
            timestamp: Date.now(),
            offset: gazeOffset,
            medianOffset,
            tolerance,
            samples: samples.length,
            keptSamples: samplesKept,
            headOffset: headAvg,
            screenAnchor: screenAnchor ?? null,
          } as any);

          console.info('[EIE][CAL] saved', {
            offset: gazeOffset,
            medianOffset,
            tolerance,
            headOffset: headAvg,
            samples: samples.length,
            keptSamples: samplesKept,
            screenAnchor: screenAnchor ?? null,
          });

          setTimeout(() => this.setters.runLightDetectionTick(), 0);
        }
      }, sampleInterval);
    } catch (err) {
      console.warn('startLocalCalibration failed:', err);
      this.setters.clearLastStableEmotion();
      this.emitter.emit('calibrationFinished', { sessionId: this.getters.getSessionId(), timestamp: Date.now(), offset: null });
    }
  }
}
